import { useEffect, useRef, useState } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@finmin/ui';
import { useAuth } from '@finmin/auth';
import { useToast } from '@finmin/ui';
import { isApiError } from '@finmin/api-client';
import { useVerifyLogin } from '@/api/auth';
import { sendFirebaseOtp, verifyFirebaseOtp } from '@/lib/firebase-phone-auth';
import { AuthLayout } from '@/features/auth/components/auth-layout';
import { StepIndicator } from '@/features/auth/components/step-indicator';
import { PhoneStep } from '@/features/auth/components/phone-step';
import { OtpStep } from '@/features/auth/components/otp-step';
import { useOtpTimer } from '@/features/auth/hooks/use-otp-timer';
import { normalizePhone, maskPhone } from '@/features/auth/utils/phone';
import {
  loginPhoneSchema,
  loginOtpSchema,
  type LoginPhoneValues,
  type LoginOtpValues,
} from '@/features/auth/types';

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * The backend returns "customer not found" either as a 404 or, in some
 * configurations, as a 400/401 with a "not found" / "does not exist" / "no
 * account" message. We treat all of these as the same "you haven't signed up
 * yet" signal so the user is gently pushed to /signup.
 */
function isCustomerNotFound(err: unknown): boolean {
  if (!isApiError(err)) return false;
  if (err.status === 404) return true;
  if (err.status === 400 || err.status === 401) {
    return /not\s*found|does\s*not\s*exist|no\s*account|no\s*such\s*(?:customer|user)|not\s*registered/i.test(
      err.message ?? '',
    );
  }
  return false;
}

// ── Route ────────────────────────────────────────────────────────────────────

/**
 * Search params for /login. `as=channel-partner` comes from the landing page's
 * "Login as Channel Partner": the same customer sign-in, landing on the
 * Channel Partner page (Hub, status or registration, decided by `/me`).
 * `redirect` is set by the `_protected` guard.
 */
interface LoginSearch {
  as?: 'channel-partner';
  redirect?: string;
}

const afterLogin = (search: LoginSearch) =>
  search.as === 'channel-partner' ? ('/channel-partner' as const) : ('/dashboard' as const);

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>): LoginSearch => ({
    as: search.as === 'channel-partner' ? 'channel-partner' : undefined,
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
  beforeLoad: ({ context, search }) => {
    if (context.auth.status === 'authenticated') {
      throw redirect({ to: afterLogin(search) });
    }
  },
  component: LoginPage,
});

// ── Page component ────────────────────────────────────────────────────────────

type LoginStep = 'phone' | 'otp';

function LoginPage() {
  const navigate = useNavigate();
  const { refresh, status } = useAuth();
  const { toast } = useToast();
  const otpTimer = useOtpTimer();
  const search = Route.useSearch();
  const destination = afterLogin(search);

  const [step, setStep] = useState<LoginStep>('phone');
  const [mobile, setMobile] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [sendOtpError, setSendOtpError] = useState<string | null>(null);
  const isVerifyingRef = useRef(false);

  // ── RHF forms ──────────────────────────────────────────────────────────────

  const phoneForm = useForm<LoginPhoneValues>({
    resolver: zodResolver(loginPhoneSchema),
    defaultValues: { mobile_e164: '' },
    mode: 'onBlur',
  });

  const otpForm = useForm<LoginOtpValues>({
    resolver: zodResolver(loginOtpSchema),
    defaultValues: { otp: '' },
  });

  // ── OTP via Firebase ───────────────────────────────────────────────────────

  async function firebaseSendOtp(phoneNumber: string) {
    if (isSendingOtp) return;
    setIsSendingOtp(true);
    setSendOtpError(null);
    try {
      await sendFirebaseOtp(phoneNumber);
      setMobile(phoneNumber);
      setOtpValue('');
      otpTimer.start();
      setStep('otp');
      toast.success('OTP sent', `Code sent to ${maskPhone(phoneNumber)}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send OTP';
      setSendOtpError(message);
      toast.error('Failed to send OTP', message);
    } finally {
      setIsSendingOtp(false);
    }
  }

  const loginNotFound = false;

  const verifyLogin = useVerifyLogin({
    onSuccess: async () => {
      toast.success('Welcome back!', 'Signing you in…');
      // Only refresh auth state here. Navigation happens in the effect below,
      // after React commits the authenticated status into the router context —
      // otherwise `_protected` reads a stale `unauthenticated` and bounces
      // straight back to /login.
      await refresh();
    },
    onError: (err) => {
      setOtpValue('');
      toast.error('Verification failed', err.message);
    },
  });

  // Navigate in once auth flips to authenticated (post-commit → fresh guard
  // context).
  useEffect(() => {
    if (status === 'authenticated') {
      void navigate({ to: destination });
    }
  }, [status, navigate, destination]);

  // ── Handlers ───────────────────────────────────────────────────────────────

  function handlePhoneSubmit(values: LoginPhoneValues) {
    void firebaseSendOtp(normalizePhone(values.mobile_e164));
  }

  async function handleOtpSubmit(values: LoginOtpValues) {
    if (isVerifyingRef.current || verifyLogin.isPending) return;
    isVerifyingRef.current = true;
    try {
      const idToken = await verifyFirebaseOtp(values.otp);
      verifyLogin.mutate({ id_token: idToken });
    } catch {
      setOtpValue('');
      toast.error('Verification failed', 'Invalid or expired OTP');
    } finally {
      isVerifyingRef.current = false;
    }
  }

  function handleResend() {
    void firebaseSendOtp(mobile);
  }

  function handleBack() {
    setStep('phone');
    setOtpValue('');
    otpTimer.reset();
    otpForm.reset();
    verifyLogin.reset();
    setSendOtpError(null);
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <AuthLayout>
      <StepIndicator current={step === 'phone' ? 0 : 1} total={2} />

      {search.as === 'channel-partner' && (
        <p className="mb-4 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          Channel Partner login — sign in with your FinminOS mobile number to open your Channel
          Partner Hub.
        </p>
      )}

      <div id="recaptcha-container" />

      {step === 'phone' ? (
        <Form {...phoneForm}>
          <PhoneStep
            isPending={isSendingOtp}
            apiError={loginNotFound ? null : sendOtpError}
            notFound={loginNotFound}
            onSubmit={handlePhoneSubmit}
          />
        </Form>
      ) : (
        <Form {...otpForm}>
          <OtpStep
            mobile={mobile}
            otpValue={otpValue}
            setOtpValue={setOtpValue}
            isPending={verifyLogin.isPending}
            apiError={verifyLogin.error?.message ?? null}
            resendSecondsLeft={otpTimer.secondsLeft}
            isResending={isSendingOtp}
            submitLabel="Verify & Sign in"
            onSubmit={handleOtpSubmit}
            onResend={handleResend}
            onBack={handleBack}
          />
        </Form>
      )}
    </AuthLayout>
  );
}
